import React, { useMemo } from 'react';
import { X, Truck, CreditCard, History, Check, ShieldCheck, Package, XCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatMoney as fmtMoney, normalizeCurrencyRates } from '../../../util/money';
import { approveOrder } from '../../../features/orderSlice';
import { resolveOrderUsdValue } from '../../../util/orderCurrency';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const formatAddress = (addr) => {
  if (!addr) return '—';
  const parts = [addr.address, addr.city, addr.postalCode, addr.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const getDisplayId = (o) => String(o?.orderId || o?._id || o?.id || '');

const OrderDetails = ({ order, onClose, hideFullViewButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currencyPayload = useSelector((state) => state.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);
  const money = (amount, from = 'USD') => fmtMoney(amount, { from, to: from });

  const { updateStatus, updateError, orders: storeOrders = [], order: storeOrder } = useSelector((state) => state.order || {});

  const orderId = String(order?._id || order?.id || '');
  const storeMatch = storeOrders.find((o) => String(o?._id || o?.id || '') === orderId);
  const resolvedOrder = storeMatch || (storeOrder && String(storeOrder?._id || storeOrder?.id || '') === orderId ? storeOrder : order);

  const hasOrder = !!resolvedOrder;
  const mongoId = String(resolvedOrder?._id || resolvedOrder?.id || '');
  const displayId = getDisplayId(resolvedOrder);
  const createdAt = resolvedOrder?.createdAt;

  const items = Array.isArray(resolvedOrder?.items) ? resolvedOrder.items : [];
  const currency =
    resolvedOrder?.paidCurrency ||
    resolvedOrder?.paymentResult?.currency ||
    resolvedOrder?.currency ||
    items?.[0]?.currency ||
    'USD';
  const usdValue = resolveOrderUsdValue(resolvedOrder, rates);
  const usdTotal = Number.isFinite(usdValue.amount)
    ? money(usdValue.amount, 'USD')
    : 'Unavailable: historical rate missing';
  const usdTotalLabel =
    usdValue.source === 'current_rate_estimate'
      ? 'USD estimate'
      : usdValue.source === 'manual_correction'
        ? 'USD corrected value'
      : 'USD equivalent';
  const isAliExpressOrdered = !!(
    resolvedOrder?.externalOutOrderId ||
    (Array.isArray(resolvedOrder?.externalOrderIds) && resolvedOrder.externalOrderIds.length) ||
    resolvedOrder?.externalStatus
  );
  const allStatusCompleted = !!resolvedOrder?.isApproved;
  const logesticCost = money(Number(resolvedOrder?.shippingPrice || 0), currency);

  const statusSteps = [
    {
      key: 'not-approved',
      label: 'Not Approved',
      done: !resolvedOrder?.isApproved,
      at: resolvedOrder?.isApproved ? null : resolvedOrder?.createdAt,
      icon: XCircle,
    },
    {
      key: 'approved',
      label: 'Approved',
      done: !!resolvedOrder?.isApproved,
      at: resolvedOrder?.approvedAt,
      icon: ShieldCheck,
    },
    {
      key: 'aliexpress',
      label: 'AliExpress',
      done: allStatusCompleted || isAliExpressOrdered,
      at: resolvedOrder?.external?.createdAt || resolvedOrder?.external?.created_at || null,
      icon: Package,
    },
    {
      key: 'logestic',
      label: 'Logestic',
      done: allStatusCompleted || !!resolvedOrder?.isShipped,
      at: resolvedOrder?.shippedAt,
      icon: Truck,
      detail: `Cost of Logestic: ${logesticCost}`,
    },
    {
      key: 'delivered',
      label: 'Delivered',
      done: allStatusCompleted || !!resolvedOrder?.isDelivered,
      at: resolvedOrder?.deliveredAt,
      icon: Check,
    },
  ];

  const canApprove = hasOrder && !resolvedOrder?.isApproved && !!mongoId;
  const isApproving = updateStatus === 'loading';

  const handleApprove = () => {
    if (!canApprove || isApproving) return;
    dispatch(approveOrder({ id: mongoId }));
  };

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-slate-200 p-5">
        <SectionHead
          title="Order Details"
          subtitle={hasOrder ? `Order #${displayId}${createdAt ? ` • ${formatDateTime(createdAt)}` : ''}` : 'No order selected'}
          right={
            <div className="flex items-center gap-2">
              {!hideFullViewButton && mongoId ? (
                <button
                  onClick={() => navigate(`/administrator/orders/details/${mongoId}`)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  Full view
                </button>
              ) : null}

              {canApprove ? (
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className={cx(
                    'inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-black text-white hover:bg-primary/90',
                    isApproving ? 'opacity-60 cursor-not-allowed' : ''
                  )}
                >
                  <ShieldCheck className="w-4 h-4" />
                  {isApproving ? 'Approving…' : 'Approve'}
                </button>
              ) : (
                <span className={cx(
                  'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-black',
                  resolvedOrder?.isApproved ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700'
                )}>
                  <ShieldCheck className="w-4 h-4" />
                  {resolvedOrder?.isApproved ? 'Approved' : 'Not Approved'}
                </span>
              )}

              <span
                className={cx(
                  'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-black',
                  resolvedOrder?.isPaid
                    ? 'bg-primary text-white shadow-[0_0_12px_rgba(210,4,45,0.35)]'
                    : 'bg-slate-200 text-slate-600'
                )}
              >
                <CreditCard className="w-4 h-4" />
                {resolvedOrder?.isPaid ? 'PAID' : 'UNPAID'}
              </span>

              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>
          }
        />

        {updateStatus === 'failed' && updateError ? (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {typeof updateError === 'string' ? updateError : (updateError?.error || updateError?.message || 'Failed to update order')}
          </div>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4 flex items-center gap-2">
              <Check size={18} className="text-primary" /> Order Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-slate-500">Order ID</p>
                <p className="mt-0.5 font-mono text-sm font-black text-primary">{displayId || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Database ID</p>
                <p className="mt-0.5 font-mono text-xs font-black text-slate-700 break-all">{mongoId || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Order Date</p>
                <p className="mt-0.5 font-semibold text-slate-900">{createdAt ? formatDateTime(createdAt) : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Total Amount</p>
                <p className="mt-0.5 font-black text-lg text-slate-900">{money(Number(resolvedOrder?.totalPrice || 0), currency)}</p>
                <p className="mt-0.5 text-xs font-semibold text-slate-500">{usdTotalLabel}: {usdTotal}</p>
              </div>
            </div>
          </Card>

          {/* Status Timeline */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4 flex items-center gap-2">
              <History size={18} className="text-primary" /> Status Timeline
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {statusSteps.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} className={cx(
                    'rounded-2xl border p-4',
                    s.done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'
                  )}>
                    <div className="flex items-center gap-2">
                      <Icon className={cx('w-4 h-4', s.done ? 'text-emerald-700' : 'text-slate-500')} />
                      <p className={cx('text-sm font-black', s.done ? 'text-emerald-800' : 'text-slate-800')}>{s.label}</p>
                    </div>
                    <p className={cx('mt-1 text-xs', s.done ? 'text-emerald-700' : 'text-slate-600')}>
                      {s.done ? (s.at ? formatDateTime(s.at) : 'Done') : 'Not yet'}
                    </p>
                    {s.detail ? (
                      <p className={cx('mt-1 text-[11px] font-semibold', s.done ? 'text-emerald-700' : 'text-slate-600')}>
                        {s.detail}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Items */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4">Order Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Selected SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-sm text-slate-500 text-center">No items</td>
                    </tr>
                  ) : (
                    items.map((item, idx) => {
                      const qty = Number(item?.quantity || 0);
                      const price = Number(item?.price || 0);
                      const lineTotal = qty * price;
                      return (
                        <tr key={`${item?.productId || item?.product || idx}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                            <div className="flex items-center gap-3">
                              {item?.image ? (
                                <img src={item.image} alt={item?.name || 'Item'} className="h-10 w-10 rounded-xl object-cover border border-slate-200" />
                              ) : (
                                <div className="h-10 w-10 rounded-xl bg-slate-100 border border-slate-200" />
                              )}
                              <div className="min-w-0">
                                <p className="truncate">{item?.name || '—'}</p>
                                {item?.productId ? (
                                  <p className="text-xs text-slate-500 font-mono truncate">{item.productId}</p>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{qty}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{money(price, currency)}</td>
                          <td className="px-4 py-3 text-xs font-black text-slate-700">
                            <span className="font-mono break-all">{String(item?.sku_attr || item?.sku_id || '—')}</span>
                          </td>
                          <td className="px-4 py-3 text-sm font-black text-slate-900">{money(lineTotal, currency)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pricing */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4">Pricing Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Items</span>
                <span className="font-semibold text-slate-900">{money(Number(resolvedOrder?.itemsPrice || 0), currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Shipping</span>
                <span className="font-semibold text-slate-900">{money(Number(resolvedOrder?.shippingPrice || 0), currency)}</span>
              </div>
              {Number(resolvedOrder?.discountPrice || 0) > 0 ? (
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Discount</span>
                  <span className="font-semibold text-emerald-700">
                    - {money(Number(resolvedOrder.discountPrice), currency)}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Tax</span>
                <span className="font-semibold text-slate-900">{money(Number(resolvedOrder?.taxPrice || 0), currency)}</span>
              </div>
              <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                <span className="text-slate-900 font-black">Total</span>
                <span className="text-slate-900 font-black">{money(Number(resolvedOrder?.totalPrice || 0), currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">{usdTotalLabel}</span>
                <span className="font-semibold text-slate-700">{usdTotal}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Information */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4">Customer Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold text-slate-500">Name</p>
                <p className="mt-0.5 font-semibold text-slate-900">{order?.shippingAddress?.fullName || order?.billingAddress?.fullName || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Email</p>
                <p className="mt-0.5 font-semibold text-slate-900 break-all">{order?.paymentResult?.email_address || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Phone</p>
                <p className="mt-0.5 font-semibold text-slate-900">{order?.shippingAddress?.phone || order?.billingAddress?.phone || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">User</p>
                <p className="mt-0.5 font-mono text-xs font-black text-slate-700 break-all">{String(order?.user || '—')}</p>
              </div>
            </div>
          </Card>

          {/* Shipping Information */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4 flex items-center gap-2">
              <Truck size={18} className="text-primary" /> Shipping
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold text-slate-500">Method</p>
                <p className="mt-0.5 font-semibold text-slate-900">{order?.shippingMethod || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Tracking</p>
                <p className="mt-0.5 font-mono text-sm font-black text-primary break-all">{order?.tracking?.trackingNumber || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Address</p>
                <p className="mt-0.5 font-semibold text-sm text-slate-900">{formatAddress(order?.shippingAddress)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Cost</p>
                <p className="mt-0.5 font-black text-slate-900">{money(Number(resolvedOrder?.shippingPrice || 0), currency)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Tracking URL</p>
                {order?.tracking?.trackingUrl ? (
                  <a
                    href={order.tracking.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 text-sm font-black text-primary break-all hover:underline"
                  >
                    {order.tracking.trackingUrl}
                  </a>
                ) : (
                  <p className="mt-0.5 text-sm text-slate-600">—</p>
                )}
              </div>
            </div>
          </Card>

          {/* Payment Information */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard size={18} className="text-primary" /> Payment
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold text-slate-500">Method</p>
                <p className="mt-0.5 font-semibold text-slate-900">
                  {order?.paymentResult?.paymentMethodLabel || order?.paymentMethod || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Reference</p>
                <p className="mt-0.5 font-mono text-sm font-black text-slate-900 break-all">{order?.paymentResult?.id || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Status</p>
                <span className={cx(
                  'mt-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-black',
                  resolvedOrder?.isPaid
                    ? 'bg-primary text-white shadow-[0_0_12px_rgba(210,4,45,0.35)]'
                    : 'bg-slate-200 text-slate-600'
                )}>
                  {resolvedOrder?.isPaid ? 'PAID' : 'UNPAID'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OrderDetails; 
